// The kanban (PipelineKanbanView / PipelineColumnView in PipelineView.swift): one column per stage, side by side in a
// horizontally scrolling strip. Each column scrolls its own cards under a fixed header in the stage's color (name,
// won/lost mark, value, store count, share), so the headers stay in view however far a column is scrolled.

import { OctagonX, Trophy } from 'lucide-react';
import { memo, useId } from 'react';
import type { RecordPayload } from '@/contracts/models';
import type { DataItem } from '@/data/hooks';
import { MoneyText, type StoreRowLookups } from '@/features/common';
import { cn, normalizeHex, readableTextOn } from '@/ui';
import { columnSummary, pipelineTitle, type Board, type BoardColumn } from './boardModel';
import { CardList } from './CardList';

/** iOS columns are 272 pt on a phone; a little wider reads better beside a mouse. */
const COLUMN_WIDTH = 288;

export interface PipelineBoardProps {
  board: Board;
  itemOf: (id: string) => DataItem<RecordPayload> | undefined;
  lookups: StoreRowLookups;
  className?: string;
}

export function PipelineBoard({ board, itemOf, lookups, className }: PipelineBoardProps) {
  return (
    <div
      role="group"
      aria-label={`${pipelineTitle(board.pipeline)} board, ${board.columns.length} stage${board.columns.length === 1 ? '' : 's'}`}
      className={cn('flex min-h-0 items-stretch gap-3 overflow-x-auto overflow-y-hidden overscroll-x-contain gt-scroll', className)}
    >
      {board.columns.map((column) => (
        <StageColumn key={column.key} column={column} itemOf={itemOf} lookups={lookups} />
      ))}
    </div>
  );
}

interface StageColumnProps {
  column: BoardColumn;
  itemOf: PipelineBoardProps['itemOf'];
  lookups: StoreRowLookups;
}

// Memoized on the column object (rebuilt only when the board's inputs change) and the page's lookups.
const StageColumn = memo(function StageColumn({ column, itemOf, lookups }: StageColumnProps) {
  const headingId = useId();
  const fill = normalizeHex(column.stage.colorHex);
  const ink = readableTextOn(fill);
  return (
    <section
      aria-labelledby={headingId}
      className="flex min-h-0 shrink-0 flex-col overflow-hidden rounded-card border border-line bg-surface-2"
      style={{ width: COLUMN_WIDTH }}
    >
      <header
        className="flex flex-col gap-1 px-3 py-2.5"
        style={{
          backgroundColor: fill,
          backgroundImage: 'linear-gradient(180deg, rgb(255 255 255 / 0.14), rgb(255 255 255 / 0) 70%)',
          color: ink,
        }}
      >
        <div className="flex min-w-0 items-center gap-2">
          <h2 id={headingId} className="min-w-0 flex-1 truncate text-xs font-bold tracking-[0.05em] uppercase" title={column.title}>
            {column.title}
          </h2>
          {column.stage.isWon && (
            <span title="Won stage" className="inline-flex shrink-0">
              <Trophy aria-hidden className="size-3.5" />
            </span>
          )}
          {column.stage.isLost && (
            <span title="Lost stage" className="inline-flex shrink-0">
              <OctagonX aria-hidden className="size-3.5" />
            </span>
          )}
        </div>
        <div className="flex min-w-0 items-baseline gap-1.5" aria-hidden>
          <MoneyText value={column.value} compact className="text-[15px] font-bold" />
          <span className="truncate text-xs opacity-90">({column.countText})</span>
          <span className="flex-1" />
          <span className="text-xs font-semibold tabular-nums">{column.percentText}</span>
        </div>
        {/* The visual line above is hidden from assistive tech; this says it in words, after the heading. */}
        <span className="sr-only">
          {columnSummary(column, lookups.currencyCode)}
        </span>
      </header>
      {column.count === 0 ? (
        <p className="px-3 py-6 text-center text-xs text-fg-subtle">No stores in {column.title}</p>
      ) : (
        <CardList
          records={column.records}
          itemOf={itemOf}
          lookups={lookups}
          label={`Stores in ${column.title}`}
          virtualized={column.virtualized}
          className="flex-1"
        />
      )}
    </section>
  );
});
